// Role-Based Access Control (RBAC) Hook
// Provides role checking and permission management

import { useQuery } from '@tanstack/react-query';
import { useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/auth/use-auth';
import { QUERY_CONFIG } from '@/config/constants';
import { QUERY_KEYS } from '@/config/queries';
import { supabaseRpcWithRetry, supabaseQueryWithRetry } from '@/lib/networkRetry';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('useRBAC');
const RBAC_DEBUG = false;
const debugLog = (...args: unknown[]) => {
  if (RBAC_DEBUG && !import.meta.env.PROD) {
    logger.debug('[useRBAC]', { args });
  }
};

export type UserRole = 'user' | 'company_admin' | 'moderator' | 'admin' | 'super_admin';

const ROLE_PRIORITY: Record<UserRole, number> = {
  user: 0,
  company_admin: 1,
  moderator: 1,
  admin: 2,
  super_admin: 3,
};

const VALID_ROLES = Object.keys(ROLE_PRIORITY) as UserRole[];

const normalizeRoles = (roles: unknown): UserRole[] => {
  if (!Array.isArray(roles)) return [];
  return roles.filter((role): role is UserRole => {
    return typeof role === 'string' && (VALID_ROLES as readonly string[]).includes(role);
  });
};

const hasElevatedRole = (roles: Iterable<UserRole>): boolean => {
  for (const role of roles) {
    if (ROLE_PRIORITY[role] > ROLE_PRIORITY.user) {
      return true;
    }
  }
  return false;
};

interface RBACHook {
  role: UserRole | null;
  isLoading: boolean;
  hasRole: (requiredRole: UserRole) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
}

export const useRBAC = (): RBACHook => {
  const { user } = useAuth();
  debugLog('auth user', user?.id);

  const { data: roles = [], isLoading } = useQuery<UserRole[]>({
    queryKey: QUERY_KEYS.RBAC_USER_ROLES(user?.id ?? ''),
    queryFn: async () => {
      if (!user) return [];

      // Prefer batched profile data which already returns all roles
      const { data: profileData, error: profileError } = await supabaseRpcWithRetry<{
        profile?: unknown;
        roles?: unknown;
      }>(supabase, 'get_profile_data', { user_id_param: user.id });

      const aggregateRoles = new Set<UserRole>();

      if (!profileError && profileData) {
        debugLog('Loaded profile data', profileData);
        const normalized = normalizeRoles(profileData.roles);
        normalized.forEach((role) => aggregateRoles.add(role));

        const profileRole = (() => {
          const profile = profileData.profile;
          if (profile && typeof profile === 'object') {
            const rawRole = (profile as { role?: unknown }).role;
            if (
              typeof rawRole === 'string' &&
              (VALID_ROLES as readonly string[]).includes(rawRole as UserRole)
            ) {
              return rawRole as UserRole;
            }
          }
          return null;
        })();

        if (profileRole) {
          aggregateRoles.add(profileRole);
        }

        if (hasElevatedRole(aggregateRoles)) {
          return Array.from(aggregateRoles);
        }
      } else if (profileError) {
        logger.error('Failed to fetch roles via get_profile_data', profileError, {
          userId: user.id,
        });
        debugLog('get_profile_data error', profileError);
      }

      // Secondary fallback: direct user_roles query (matches useUserRoles)
      const { data: directRoles, error: directError } = await supabaseQueryWithRetry(() =>
        supabase.from('user_roles').select('role').eq('user_id', user.id),
      );

      if (directError) {
        logger.error('Failed to fetch roles from user_roles', directError, { userId: user.id });
        debugLog('user_roles error', directError);
      }

      const directRoleRows = (directRoles ?? []) as Array<{ role?: unknown }>;
      const normalizedDirect = normalizeRoles(directRoleRows.map((r) => r.role));
      debugLog('user_roles result', normalizedDirect);
      if (normalizedDirect.length && hasElevatedRole(normalizedDirect)) {
        debugLog('Using roles from user_roles', normalizedDirect);
        return normalizedDirect;
      }

      // Fallback to legacy single-role RPC (profiles.role column)
      const { data: singleRole, error } = await supabaseRpcWithRetry<UserRole | null>(
        supabase,
        'get_user_role',
      );

      if (error) {
        logger.error('Failed to fetch user role fallback', error, { userId: user.id });
        debugLog('get_user_role error', error);
      } else if (singleRole) {
        debugLog('get_user_role result', singleRole);
        const fallbackRoles = normalizeRoles([singleRole]);
        if (fallbackRoles.length) {
          debugLog('Using role from get_user_role', fallbackRoles);
          return fallbackRoles;
        }
      }

      // Final safety: read profiles.role directly (in case RPC defaulted)
      const { data: profileRow, error: profilesFallbackError } = await supabaseQueryWithRetry(() =>
        supabase.from('profiles').select('role').eq('id', user.id).maybeSingle(),
      );

      if (profilesFallbackError) {
        logger.error('Failed to read profiles.role fallback', profilesFallbackError, {
          userId: user.id,
        });
        debugLog('profiles.role error', profilesFallbackError);
        return [];
      }

      const profileRole =
        profileRow && typeof profileRow === 'object'
          ? (profileRow as { role?: unknown }).role
          : null;
      debugLog('profiles.role result', profileRole);
      const profileRoles = normalizeRoles(profileRole ? [profileRole] : []);
      debugLog('Using role from profiles table', profileRoles);
      return profileRoles;
    },
    enabled: !!user,
    staleTime: QUERY_CONFIG.STALE_TIME.LONG,
    gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
    refetchOnWindowFocus: false,
  });

  const effectiveRoles = useMemo<UserRole[]>(() => {
    return roles.length ? roles : ['user'];
  }, [roles]);

  const primaryRole = useMemo<UserRole>(() => {
    return effectiveRoles.reduce((currentHighest, role) => {
      return ROLE_PRIORITY[role] > ROLE_PRIORITY[currentHighest] ? role : currentHighest;
    }, 'user');
  }, [effectiveRoles]);

  // Role hierarchy: super_admin > admin > company_admin > user
  const hasRole = useCallback(
    (requiredRole: UserRole): boolean => {
      return effectiveRoles.some((role) => ROLE_PRIORITY[role] >= ROLE_PRIORITY[requiredRole]);
    },
    [effectiveRoles],
  );

  // Memoize role checks
  const isSuperAdmin = useMemo(() => hasRole('super_admin'), [hasRole]);
  const isAdmin = useMemo(() => hasRole('admin'), [hasRole]);
  const isCompanyAdmin = useMemo(() => {
    return hasRole('company_admin');
  }, [hasRole]);

  debugLog(
    'state snapshot',
    JSON.stringify({
      roles: effectiveRoles,
      primaryRole,
      isAdmin,
      isSuperAdmin,
      isCompanyAdmin,
      isLoading,
    }),
  );

  return {
    role: primaryRole || null,
    isLoading,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isCompanyAdmin,
  };
};

// Audit logging utility
export const logAdminAction = async (
  action: string,
  targetId?: string,
  targetType?: string,
  details?: Record<string, unknown>,
) => {
  try {
    const { error } = await supabaseRpcWithRetry(supabase, 'log_admin_action', {
      p_action: action,
      p_target_id: targetId || null,
      p_target_type: targetType || null,
      p_details: details ? JSON.parse(JSON.stringify(details)) : null,
    });

    if (error) {
      logger.error('Failed to log admin action', error, { action, targetId, targetType });
    }
  } catch (err) {
    logger.error(
      'Exception logging admin action',
      err instanceof Error ? err : new Error(String(err)),
      {
        action,
        targetId,
        targetType,
      },
    );
  }
};
